import {combine, restore} from 'effector';

import {fetchInvoiceDetail} from 'api/documents';
import {rootDomain} from 'core/rootDomain';
import {waitDocumentInGridAndRedirectFx} from 'models/documents/create';

import {PackageTypes} from 'types/gtins';
import {InvoiceLine} from 'types/invoices';

import {
    $invoiceState,
    CreateInvoiceGate,
    updateInvoiceBodyFx,
    updateInvoiceStatusFx,
} from '../common';
import {fetchInvocieXmlFx, fetchXmlValidationFx} from '../create';

const domain = rootDomain.createDomain('InvoiceBodyDomain');

export const setCombineByGtin = domain.createEvent<boolean>();
export const $combineByGtin = restore(setCombineByGtin, true).reset(CreateInvoiceGate.close);

export const setCurrentScannedGtin = domain.createEvent<string | null>();
export const $currentScannedGtin = restore(setCurrentScannedGtin, null).reset(
    CreateInvoiceGate.close,
);

export const setModalGroupByGtinSettings = domain.createEvent<{
    lineIndex: string;
    show: boolean;
    gtin?: string;
    packagingType?: PackageTypes;
}>();
export const $modalGroupByGtinSettings = restore(setModalGroupByGtinSettings, {
    lineIndex: '-1',
    show: false,
    gtin: undefined,
    packagingType: undefined,
}).reset(CreateInvoiceGate.close);

export const $inlineInvoiceChargeModal = domain.createStore<{
    index?: string;
    show: boolean;
}>({
    index: undefined,
    show: false,
});

export const setInlineInvoiceChargeModal = domain.createEvent<{
    index?: string;
    show: boolean;
}>();

export const $inlineModalFormReadonly = domain.createStore<{
    index: string;
    show: boolean;
}>({
    index: '-1',
    show: false,
});

export const setInlineModalFormReadonly = domain.createEvent<{
    index: string;
    show: boolean;
}>();

export const $modalDuplicateLinesModal = domain.createStore<{
    index?: number;
    show: boolean;
    initialLineData?: Partial<InvoiceLine>;
}>({
    show: false,
});

export const setModalDuplicateLinesModal = domain.createEvent<{
    index?: number;
    show: boolean;
    initialLineData?: Partial<InvoiceLine>;
}>();

export const setModalTransportCodesModal = domain.createEvent<{
    show: boolean;
    id: string;
}>();
export const $modalTransportCodesModal = domain.createStore<{
    show: boolean;
    id: string;
}>({
    show: false,
    id: '',
});

export const fetchDataFx = domain.createEffect({
    name: 'fetchDataFx',
    handler: fetchInvoiceDetail,
});

export const commonData$ = domain.createStore<string | null>(null);

export const $everythingReady = combine($invoiceState, commonData$, (allDone, extraDone) => {
    if (allDone === 'new') {
        return true;
    }
    return allDone && extraDone;
}).reset(CreateInvoiceGate.close);

export const setModalViewCodesDropdown = domain.createEvent<string>();
export const $modalViewCodesDropdown = restore(setModalViewCodesDropdown, 'qwe').reset(
    CreateInvoiceGate.close,
);

export const $commonDataState = combine({
    data: commonData$,
    isLoading: fetchDataFx.pending,
}).reset(CreateInvoiceGate.close);

$inlineInvoiceChargeModal.reset(CreateInvoiceGate.close);
$modalDuplicateLinesModal.reset(CreateInvoiceGate.close);
$modalTransportCodesModal.reset(CreateInvoiceGate.close);

export const $invoiceCreatingPending = combine(
    updateInvoiceStatusFx.pending,
    updateInvoiceBodyFx.pending,
    fetchXmlValidationFx.pending,
    fetchInvocieXmlFx.pending,
    waitDocumentInGridAndRedirectFx.pending,
    (
        updateInvoiceStatusPending,
        updateInvoiceBodyPending,
        fetchXmlValidationPending,
        fetchInvocieXmlPending,
        waitDocumentInGridPending,
    ) =>
        updateInvoiceStatusPending ||
        updateInvoiceBodyPending ||
        fetchXmlValidationPending ||
        fetchInvocieXmlPending ||
        waitDocumentInGridPending,
);

export const setInvoiceCreatingPending = domain.createEvent<boolean>();
